import { BUILTIN_CHAT_SECURITY_PATTERNS } from '../../data/alert-risk-text';
import { clientDisplayName, fillRiskTemplate, partsOf, riskCopy, } from './common';
/** The exact source suffixes each producer appends (cursor-chat/index.ts). */
const SOURCE_SUFFIXES = {
    'risky-code': [' (in AI-generated code block)', ' (in user code block)'],
    'dangerous-command': [' (in AI-generated command)', ' (in user command)'],
};
const EXECUTED_MARKER = ' (executed: ';
/** Names the scanner uses when a pattern has no description (row 28, 41). */
const SCANNER_DEFAULT_NAMES = new Set([
    'Malicious pattern match',
    'Malicious command execution',
]);
/**
 * The pattern name from a chat finding description (rows 18, 31): exactly one
 * of the kind's source suffixes is removed, never "the last bracket" (built-in
 * names end in brackets). Commands then lose everything from the last
 * " (executed: " (an unvalidated command name); a second marker is
 * ambiguous and returns undefined, the static fallback.
 */
export function stripChatRuleDescription(description, kind) {
    const suffix = SOURCE_SUFFIXES[kind].find(s => description.endsWith(s));
    const name = suffix == null ? description : description.slice(0, -suffix.length);
    if (kind === 'risky-code')
        return name;
    const executed = name.lastIndexOf(EXECUTED_MARKER);
    if (executed === -1)
        return name;
    const remainder = name.slice(0, executed);
    return remainder.includes(EXECUTED_MARKER) ? undefined : remainder;
}
/** A pattern name the copy may quote: non-empty and not a scanner default. */
function quotableName(name) {
    const trimmed = name?.trim() ?? '';
    return trimmed.length === 0 || SCANNER_DEFAULT_NAMES.has(trimmed)
        ? undefined
        : trimmed;
}
/**
 * Rows 18–28 and 31–41. A built-in bucket (K1–K10) needs the stored regex and
 * the name to match a current built-in exactly; anything else is K0, including
 * alerts stored before `detectionPattern` existed (Security decision 8).
 */
export function resolveChatPattern(alert, kind) {
    const name = stripChatRuleDescription(alert.description, kind);
    const builtin = alert.detectionPattern == null
        ? undefined
        : BUILTIN_CHAT_SECURITY_PATTERNS.find(entry => entry.pattern === alert.detectionPattern &&
            entry.description === name);
    const mapping = kind === 'risky-code'
        ? 'maliciousPatterns::risky-code'
        : 'commandPatterns::dangerous-command';
    const copy = riskCopy(`${mapping}#${builtin?.bucket ?? 'K0'}`);
    return partsOf(copy, fillRiskTemplate(copy.whatHappened, {
        client: clientDisplayName(alert.clientType),
        ruleDescription: quotableName(name),
    }), copy.whyItMatters);
}
/** Row 29: credential-exposure quotes the org pattern name, if any. */
export function resolveCredentialExposure(alert) {
    const copy = riskCopy('maliciousPatterns::credential-exposure');
    return partsOf(copy, fillRiskTemplate(copy.whatHappened, {
        client: clientDisplayName(alert.clientType),
        // Row 29: as for the code-pattern rows.
        ruleDescription: quotableName(stripChatRuleDescription(alert.description, 'risky-code')),
    }), copy.whyItMatters);
}
//# sourceMappingURL=chat.js.map